import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { Category } from '@modules/common/types';
import {
  getFieldNameFromEqlField,
  getMetadataQBFieldFromEqlField,
} from '@modules/common/utils';
import {
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { trackQuantifyCompareEvents } from '@modules/quantify/utils';
import { getQuickFilterParamsForField } from '@modules/queryBuilder/helpers';
import { useAddQuickFilter } from '@modules/queryBuilder/hooks';
import { QuickFilterParams } from '@modules/queryBuilder/hooks/useAddQuickFilter';
import { FilterListRounded } from '@mui/icons-material';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from '@mui/material';

interface CompareAddToFilterActionProps {
  metricKeyId: string;
  metricCategory?: Category;
  shouldShowAddToFilterForBothCompareFields?: boolean;
  isMenuItem?: boolean;
}

export const CompareAddToFilterAction = ({
  metricKeyId,
  metricCategory,
  shouldShowAddToFilterForBothCompareFields = true,
  isMenuItem = false,
}: CompareAddToFilterActionProps) => {
  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const isCompareByFilter = useQuantifyStore(
    ({ query }) => !!query.compareQuery?.whereClause,
  );
  const compareQBFieldA = useQuantifyStore(({ misc }) => misc.compareQBFieldA);
  const compareQBFieldB = useQuantifyStore(({ misc }) => misc.compareQBFieldB);
  const compareFilterAName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.baseQueryName || DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.deltaQueryName || DEFAULT_COMPARE_FILTER_B_NAME,
  );

  const analysisLink = useAnalysisStore(({ href }) => href);

  const addDefaultQuickFilter = useAddQuickFilter();
  const addQuickFilterFieldA = useAddQuickFilter({
    customQBStoreFields: compareQBFieldA
      ? {
          dispatch: compareQBFieldA.dispatch,
          rootID: compareQBFieldA.rootID,
          nodesByID: compareQBFieldA.nodesByID,
        }
      : undefined,
  });
  const addQuickFilterFieldB = useAddQuickFilter({
    customQBStoreFields: compareQBFieldB
      ? {
          dispatch: compareQBFieldB.dispatch,
          rootID: compareQBFieldB.rootID,
          nodesByID: compareQBFieldB.nodesByID,
        }
      : undefined,
  });

  const fieldName = groupBy ? getFieldNameFromEqlField(groupBy) : undefined;
  const metadataQbField = groupBy
    ? getMetadataQBFieldFromEqlField(groupBy)
    : undefined;

  const appendQuickFilter = (
    addFilterCallback: (filters: QuickFilterParams[]) => void,
  ) => {
    if (fieldName && metricKeyId) {
      const quickFilterParams = getQuickFilterParamsForField({
        fieldName,
        metadataQbField: metadataQbField,
        value: metricKeyId,
        category: metricCategory,
      });
      addFilterCallback(quickFilterParams);
    }
  };

  if (isMenuItem) {
    return isCompareByFilter && shouldShowAddToFilterForBothCompareFields ? (
      <>
        <MenuItem
          onClick={() => {
            appendQuickFilter(addQuickFilterFieldA);
            trackQuantifyCompareEvents({
              eventName: 'Quantify Compare - Quick Filter Added For Filter A',
              link: analysisLink,
            });
          }}
        >
          <ListItemIcon>
            <FilterListRounded
              sx={{
                color: 'text.disabled',
              }}
            />
          </ListItemIcon>
          <ListItemText primary={`Add to ${compareFilterAName}`} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            appendQuickFilter(addQuickFilterFieldB);
            trackQuantifyCompareEvents({
              eventName: 'Quantify Compare - Quick Filter Added For Filter B',
              link: analysisLink,
            });
          }}
        >
          <ListItemIcon>
            <FilterListRounded
              sx={{
                color: 'text.disabled',
              }}
            />
          </ListItemIcon>
          <ListItemText primary={`Add to ${compareFilterBName}`} />
        </MenuItem>
      </>
    ) : (
      <MenuItem
        onClick={() => {
          appendQuickFilter(addDefaultQuickFilter);
          trackQuantifyCompareEvents({
            eventName: 'Quantify Compare - Quick Filter Added',
            link: analysisLink,
          });
        }}
      >
        <ListItemIcon>
          <FilterListRounded
            sx={{
              color: 'text.disabled',
            }}
          />
        </ListItemIcon>
        <ListItemText primary="Add to Filter" />
      </MenuItem>
    );
  }

  return isCompareByFilter && shouldShowAddToFilterForBothCompareFields ? (
    <>
      <ListItem
        divider
        disablePadding
        onClick={() => {
          appendQuickFilter(addQuickFilterFieldA);
          trackQuantifyCompareEvents({
            eventName: 'Quantify Compare - Quick Filter Added For Filter A',
            link: analysisLink,
          });
        }}
      >
        <ListItemButton>
          <ListItemIcon>
            <FilterListRounded
              sx={{
                color: 'text.disabled',
              }}
            />
          </ListItemIcon>
          <ListItemText primary={`Add to ${compareFilterAName}`} />
        </ListItemButton>
      </ListItem>
      <ListItem
        disablePadding
        onClick={() => {
          appendQuickFilter(addQuickFilterFieldB);
          trackQuantifyCompareEvents({
            eventName: 'Quantify Compare - Quick Filter Added For Filter B',
            link: analysisLink,
          });
        }}
      >
        <ListItemButton>
          <ListItemIcon>
            <FilterListRounded
              sx={{
                color: 'text.disabled',
              }}
            />
          </ListItemIcon>
          <ListItemText primary={`Add to ${compareFilterBName}`} />
        </ListItemButton>
      </ListItem>
    </>
  ) : (
    <ListItem
      disablePadding
      onClick={() => {
        appendQuickFilter(addDefaultQuickFilter);
        trackQuantifyCompareEvents({
          eventName: 'Quantify Compare - Quick Filter Added',
          link: analysisLink,
        });
      }}
    >
      <ListItemButton>
        <ListItemIcon>
          <FilterListRounded
            sx={{
              color: 'text.disabled',
            }}
          />
        </ListItemIcon>
        <ListItemText primary="Add to Filter" />
      </ListItemButton>
    </ListItem>
  );
};
